import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { signInWithCustomToken } from "firebase/auth";
import { track } from "@/lib/analytics";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Sheet } from "@/components/shell/Sheet";
import { InstallHintBar } from "@/components/auth/InstallHintBar";
import { useAuth } from "@/lib/auth/context";
import { getFirebaseAuth } from "@/lib/firebase/client";
import { toast } from "@/components/shell/Toaster";
import { buildKidsFakeEmail } from "@/lib/db/members";
import {
  householdSlugSchema,
  memberUsernameSchema,
} from "@/lib/schemas";
import { normalizeUsername } from "@/lib/utils/slug";

type Mode = "adult" | "child";

const adultSchema = z.object({
  email: z.string().email("Niepoprawny email"),
  password: z.string().min(6, "Min. 6 znaków"),
});
type AdultValues = z.infer<typeof adultSchema>;

const childSchema = z.object({
  username: memberUsernameSchema,
  slug: householdSlugSchema,
  password: z.string().min(6, "Min. 6 znaków"),
});
type ChildValues = z.infer<typeof childSchema>;

const STORAGE_LAST_SLUG = "kd_last_household_slug";

export default function LoginPage() {
  const navigate = useNavigate();
  const {
    signIn,
    signInWithGoogle,
    resetPassword,
    firebaseUser,
    userDoc,
    loading,
  } = useAuth();
  // Prefill z QR karty logowania dziecka (AddChildSheet):
  // /login?token=<customToken> → automatyczne logowanie bez wpisywania
  //   (signInWithCustomToken; token z mintChildLoginToken CF, ważny ~1h).
  // /login?slug=<rodzina>&username=<nazwa> → fallback: tryb dziecka,
  //   oba pola wpisane, zostaje hasło. Hasła NIGDY nie przenosimy w URL.
  const [searchParams] = useSearchParams();
  const qsSlug = normalizeUsername(searchParams.get("slug") ?? "");
  const qsUsername = normalizeUsername(searchParams.get("username") ?? "");
  const qsToken = searchParams.get("token");

  const [mode, setMode] = useState<Mode>(
    qsSlug || qsUsername || qsToken ? "child" : "adult",
  );
  const [tokenBusy, setTokenBusy] = useState(!!qsToken);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!qsToken) return;
    let cancelled = false;
    signInWithCustomToken(getFirebaseAuth(), qsToken)
      .then(() => {
        // Redirect zrobi useEffect na firebaseUser+userDoc niżej.
        track("child_logged_in", { method: "qr_token" });
        toast.success("Zalogowano");
      })
      .catch(() => {
        if (cancelled) return;
        setTokenBusy(false);
        setServerError(
          "Kod logowania wygasł lub jest nieprawidłowy. Poproś rodzica o nowy kod albo zaloguj się hasłem.",
        );
      });
    return () => {
      cancelled = true;
    };
    // Jednorazowo przy wejściu — token z URL-a nie zmienia się w trakcie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const adultForm = useForm<AdultValues>({ resolver: zodResolver(adultSchema) });
  const childForm = useForm<ChildValues>({
    resolver: zodResolver(childSchema),
    defaultValues: {
      username: qsUsername,
      slug:
        qsSlug ||
        (typeof window !== "undefined"
          ? localStorage.getItem(STORAGE_LAST_SLUG) || ""
          : ""),
    },
  });

  const openReset = () => {
    setResetEmail(adultForm.getValues("email") || "");
    setResetError(null);
    setResetOpen(true);
  };

  const handleReset = async () => {
    const email = resetEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setResetError("Wpisz prawidłowy email");
      return;
    }
    setResetBusy(true);
    setResetError(null);
    try {
      await resetPassword(email);
      toast.success(`Wysłaliśmy link resetu na ${email}`);
      setResetOpen(false);
    } catch (e) {
      setResetError(e instanceof Error ? e.message : "Błąd");
    } finally {
      setResetBusy(false);
    }
  };

  useEffect(() => {
    if (!loading && firebaseUser && userDoc) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, firebaseUser, userDoc, navigate]);

  const onAdult = adultForm.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await signIn(values.email, values.password);
      toast.success("Zalogowano");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setServerError(e instanceof Error ? e.message : "Błąd logowania");
    }
  });

  const onChild = childForm.handleSubmit(async (values) => {
    setServerError(null);
    const slug = values.slug.toLowerCase().trim();
    const username = values.username.toLowerCase().trim();
    const fakeEmail = buildKidsFakeEmail(username, slug);
    try {
      await signIn(fakeEmail, values.password);
      // Cache slug do następnego razu (mniej wpisywania)
      localStorage.setItem(STORAGE_LAST_SLUG, slug);
      track("child_logged_in", {
        method: "password",
        prefilled: !!(qsSlug || qsUsername),
      });
      toast.success("Zalogowano");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Błąd logowania";
      // Firebase auth/user-not-found = brak takiego username w tej rodzinie
      if (msg.includes("user-not-found") || msg.includes("invalid-credential")) {
        setServerError("Nie ma takiego użytkownika lub hasło nie pasuje.");
      } else {
        setServerError(msg);
      }
    }
  });

  const handleGoogle = async () => {
    setServerError(null);
    setGoogleBusy(true);
    try {
      await signInWithGoogle();
      toast.success("Zalogowano");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setServerError(e instanceof Error ? e.message : "Błąd logowania");
    } finally {
      setGoogleBusy(false);
    }
  };

  const inputCls =
    "mt-1 w-full h-11 rounded-[12px] border border-line bg-card px-3 text-sm focus:outline-none focus:border-accent";

  // Logowanie kodem QR w toku — pełnoekranowy stan zamiast formularzy.
  if (tokenBusy) {
    return (
      <main className="min-h-dvh flex items-center justify-center bg-bg text-ink">
        <div className="flex flex-col items-center gap-3 text-center">
          <Logo variant="mark" size={56} />
          <p className="text-sm text-muted">Logowanie kodem…</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-dvh flex flex-col bg-bg text-ink">
      <div className="mx-auto w-full max-w-md px-4 pt-12 pb-6 flex-1 flex flex-col">
        <div className="flex flex-col items-center text-center mb-6">
          <Logo variant="mark" size={56} />
          <h1 className="mt-4 text-2xl font-semibold tracking-tight">
            Witaj w Sianko
          </h1>
          <p className="mt-1 text-sm text-muted">
            Zadania, kasa, streak — bez chaosu.
          </p>
        </div>

        <InstallHintBar />

        <div className="grid grid-cols-2 gap-2 mb-3">
          {(["adult", "child"] as Mode[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setMode(m);
                setServerError(null);
              }}
              className={`h-11 rounded-[12px] text-sm font-medium border transition-colors ${
                mode === m
                  ? "bg-ink text-card border-ink"
                  : "bg-card text-ink border-line hover:bg-soft"
              }`}
            >
              {m === "adult" ? "Rodzic / opiekun" : "Dziecko"}
            </button>
          ))}
        </div>

        <Card>
          <CardBody className="pt-5">
            {mode === "adult" ? (
              <>
                <form onSubmit={onAdult} className="space-y-3">
                  <label className="block">
                    <span className="text-xs font-medium text-muted">
                      E-mail
                    </span>
                    <input
                      type="email"
                      autoComplete="email"
                      placeholder="ty@example.com"
                      className={inputCls}
                      {...adultForm.register("email")}
                    />
                    {adultForm.formState.errors.email && (
                      <p className="mt-1 text-xs text-danger">
                        {adultForm.formState.errors.email.message}
                      </p>
                    )}
                  </label>
                  <label className="block">
                    <span className="text-xs font-medium text-muted">
                      Hasło
                    </span>
                    <input
                      type="password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      className={inputCls}
                      {...adultForm.register("password")}
                    />
                    {adultForm.formState.errors.password && (
                      <p className="mt-1 text-xs text-danger">
                        {adultForm.formState.errors.password.message}
                      </p>
                    )}
                  </label>
                  {serverError && (
                    <p className="text-sm text-danger">{serverError}</p>
                  )}
                  <Button
                    type="submit"
                    variant="accent"
                    size="lg"
                    block
                    disabled={adultForm.formState.isSubmitting}
                  >
                    {adultForm.formState.isSubmitting
                      ? "Logowanie…"
                      : "Zaloguj się"}
                  </Button>
                  <button
                    type="button"
                    onClick={openReset}
                    className="block w-full text-center text-xs text-muted hover:text-ink"
                  >
                    Zapomniałem hasła
                  </button>
                </form>

                <div className="my-4 flex items-center gap-3 text-[11px] text-muted">
                  <div className="h-px flex-1 bg-line" /> lub
                  <div className="h-px flex-1 bg-line" />
                </div>

                <Button
                  variant="secondary"
                  size="lg"
                  block
                  onClick={handleGoogle}
                  disabled={googleBusy}
                >
                  {googleBusy ? "…" : "Kontynuuj z Google"}
                </Button>
              </>
            ) : (
              <form onSubmit={onChild} className="space-y-3">
                <p className="text-xs text-muted">
                  Wpisz swoją nazwę użytkownika, pseudonim rodziny i hasło.
                  Dostałeś/aś je od rodzica.
                </p>
                <label className="block">
                  <span className="text-xs font-medium text-muted">
                    Nazwa użytkownika
                  </span>
                  <input
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="hania"
                    className={inputCls}
                    {...childForm.register("username", {
                      // Live normalize: dziecko pisze "Kasia" → "kasia".
                      onChange: (e) => {
                        const next = normalizeUsername(e.target.value);
                        childForm.setValue("username", next, {
                          shouldValidate: false,
                        });
                        e.target.value = next;
                      },
                    })}
                  />
                  {childForm.formState.errors.username && (
                    <p className="mt-1 text-xs text-danger">
                      {childForm.formState.errors.username.message}
                    </p>
                  )}
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-muted">
                    Rodzina
                  </span>
                  <input
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="kowalski"
                    className={inputCls}
                    {...childForm.register("slug", {
                      // Live normalize slug-a rodziny tak samo jak username.
                      onChange: (e) => {
                        const next = normalizeUsername(e.target.value);
                        childForm.setValue("slug", next, {
                          shouldValidate: false,
                        });
                        e.target.value = next;
                      },
                    })}
                  />
                  {childForm.formState.errors.slug && (
                    <p className="mt-1 text-xs text-danger">
                      {childForm.formState.errors.slug.message}
                    </p>
                  )}
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-muted">Hasło</span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className={inputCls}
                    {...childForm.register("password")}
                  />
                  {childForm.formState.errors.password && (
                    <p className="mt-1 text-xs text-danger">
                      {childForm.formState.errors.password.message}
                    </p>
                  )}
                </label>
                {serverError && (
                  <p className="text-sm text-danger">{serverError}</p>
                )}
                <Button
                  type="submit"
                  variant="accent"
                  size="lg"
                  block
                  disabled={childForm.formState.isSubmitting}
                >
                  {childForm.formState.isSubmitting
                    ? "Logowanie…"
                    : "Zaloguj się"}
                </Button>
                <p className="text-[11px] text-center text-muted pt-1">
                  Zapomniałeś/aś hasła? Poproś rodzica — może je zresetować
                  w aplikacji.
                </p>
              </form>
            )}
          </CardBody>
        </Card>

        {mode === "adult" && (
          <div className="mt-6 text-center text-sm text-muted">
            Nie masz konta?{" "}
            <Link to="/signup" className="text-accent font-medium">
              Załóż
            </Link>
          </div>
        )}
      </div>

      <Sheet
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        title="Reset hasła"
      >
        <div className="space-y-3">
          <p className="text-sm text-muted">
            Podaj e-mail przypisany do konta — wyślemy link do ustawienia
            nowego hasła.
          </p>
          <label className="block">
            <span className="text-xs font-medium text-muted">E-mail</span>
            <input
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="ty@example.com"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="mt-1 w-full h-11 rounded-[12px] border border-line bg-card px-3 text-sm focus:outline-none focus:border-accent"
            />
          </label>
          {resetError && <p className="text-sm text-danger">{resetError}</p>}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="secondary"
              onClick={() => setResetOpen(false)}
              disabled={resetBusy}
            >
              Anuluj
            </Button>
            <Button
              variant="accent"
              onClick={handleReset}
              disabled={resetBusy}
            >
              {resetBusy ? "Wysyłam…" : "Wyślij link"}
            </Button>
          </div>
        </div>
      </Sheet>
    </main>
  );
}
